import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import type { CreateRoomTypeCapacityParams } from '~/hooks/apis/reservation/useCreateRoomTypeCapacity';

import type { HttpResponse } from '~/services/types';

import { runInChunks } from '~/utils/deleteAllInChunks';
import { reportError } from '~/utils/reportError';

import { DEFAULT_CAPACITY_COMBINATIONS } from './utils';
import type { CapacityRoomRate } from './utils';

export interface ApplyCapacitiesParams {
  createRoomTypeCapacity: UseMutateAsyncFunction<HttpResponse<unknown>, unknown, CreateRoomTypeCapacityParams>;
  propertyId: string | undefined;
  targetRoomRates: Array<CapacityRoomRate>;
}

export interface ApplyCapacitiesResult {
  failedRoomRateIds: Array<string>;
  succeededRoomRateIds: Array<string>;
}

/**
 * Applies every default capacity combination to each target room rate, five room rates at a
 * time. Each room rate gets its own try/catch so one failing combination never stops the rest
 * from being attempted. Callers own applying the result to their own state/notifications.
 */
export async function applyCapacities({
  createRoomTypeCapacity,
  propertyId,
  targetRoomRates,
}: ApplyCapacitiesParams): Promise<ApplyCapacitiesResult> {
  if (!propertyId || targetRoomRates.length === 0) {
    return { failedRoomRateIds: [], succeededRoomRateIds: [] };
  }

  const failedRoomRateIds: Array<string> = [];

  await runInChunks(
    targetRoomRates,
    async (roomRate) => {
      let hasFailedCombination = false;

      for (const capacity of DEFAULT_CAPACITY_COMBINATIONS) {
        try {
          await createRoomTypeCapacity({
            capacity,
            propertyId,
            roomRateId: roomRate.roomRateId,
            roomTypeId: roomRate.roomTypeId,
          });
        } catch (error) {
          reportError(error);
          hasFailedCombination = true;
        }
      }

      if (hasFailedCombination) {
        throw new Error(`Failed to apply default capacity to room rate ${roomRate.roomRateId}`);
      }
    },
    {
      chunkSize: 5,
      failureMode: 'isolate',
      onError: (_error, roomRate) => {
        if (roomRate) {
          failedRoomRateIds.push(roomRate.roomRateId);
        }
      },
    },
  );

  const failedRoomRateIdSet = new Set(failedRoomRateIds);
  const succeededRoomRateIds = targetRoomRates
    .map((roomRate) => roomRate.roomRateId)
    .filter((id) => !failedRoomRateIdSet.has(id));

  return { failedRoomRateIds, succeededRoomRateIds };
}
